import { Card, CardHeader, CardContent, makeStyles, Typography, CircularProgress } from '@material-ui/core'
import { useMemo } from 'react'
import { standardizeStartOfDateUTC, convert24HourTimeTo12HourTime } from '../../utils'
import { gray } from '../../utils/colors'

const useStyles = makeStyles(() => ({
	cardContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1.5rem'
	},
	row: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: '1rem'
	},
	section: {
		flex: 1
	},
	overline: {
		fontSize: '0.75rem',
		fontWeight: 800,
		textTransform: 'uppercase',
		fontFamily: 'Nunito',
		color: gray[900]
	},
	mainText: {
		fontSize: '1.5rem',
		fontWeight: 800,
		fontFamily: 'Nunito',
		color: gray[800]
	},
	subtitle: {
		fontSize: '1rem',
		fontWeight: 600,
		fontFamily: 'Nunito',
		color: gray[800]
	},
	spinnerContainer: {
		position: 'absolute',
		width: '100%',
		height: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		backgroundColor: 'rgba(255, 255, 255, 0.6)'
	}
}))

const TrainingClassDetailsCard = ({ trainingClass, loading }) => {
	const classes = useStyles()

	const instructors = useMemo(() => {
		if (!trainingClass) {
			return []
		}

		const { instructor_id, instructors = [] } = trainingClass

		if (!instructors.length === 0) {
			return [instructor_id]
		}

		return instructors
	}, [trainingClass])

	const formattedDate = useMemo(() => {
		if (!trainingClass || !trainingClass.dates) {
			return ''
		}
		const { dates } = trainingClass
		const start = standardizeStartOfDateUTC(dates[0]).format('MMM DD')
		const end = standardizeStartOfDateUTC(dates[dates.length - 1]).format('MMM DD')

		return `${start}-${end}`
	}, [trainingClass])

	return (
		<Card style={{ marginTop: '24px', marginBottom: '24px', position: 'relative' }}>
			{loading ? (
				<div className={classes.spinnerContainer}>
					<CircularProgress />
				</div>
			) : null}
			<CardHeader title="Class Details" />
			<CardContent className={classes.cardContent}>
				<>
					<div className={classes.row}>
						<div className={classes.section}>
							<Typography className={classes.overline}>Type</Typography>
							<Typography variant="h1" className={classes.mainText}>
								Training
							</Typography>
							<Typography className={classes.subtitle}>
								{trainingClass?.training_course_id?.facility_id?.name || ''}
							</Typography>
						</div>
						<div className={classes.section}>
							<Typography className={classes.overline}>Date</Typography>
							<Typography variant="h1" className={classes.mainText}>
								{formattedDate}
							</Typography>
							{trainingClass?.variable_times === true ? (
								<Typography className={classes.subtitle}>Times vary</Typography>
							) : (
								<Typography className={classes.subtitle}>
									{convert24HourTimeTo12HourTime(trainingClass?.start_time)} -{' '}
									{convert24HourTimeTo12HourTime(trainingClass?.end_time)}
								</Typography>
							)}
						</div>
						<div className={classes.section}>
							<Typography className={classes.overline}>Capacity (Max)</Typography>
							<Typography variant="h1" className={classes.mainText}>
								{trainingClass?.capacity_max}
							</Typography>
						</div>
						<div className={classes.section}>
							<Typography className={classes.overline}>Location</Typography>
							<Typography variant="h1" className={classes.mainText}>
								{trainingClass?.training_location_id?.name}
							</Typography>
						</div>
					</div>
					<div className={classes.row}>
						<div className={classes.section}>
							{trainingClass?.multiple_instructors === true ? (
								<>
									<Typography className={classes.overline}>Instructors</Typography>
									<Typography className={classes.subtitle}>
										{instructors?.map((adj) => adj.full_name).join(', ')}
									</Typography>
								</>
							) : (
								<>
									<Typography className={classes.overline}>Instructor</Typography>
									<Typography className={classes.subtitle}>
										{instructors[0]?.full_name || ''}
									</Typography>
								</>
							)}
						</div>
						<div className={classes.section}>
							<Typography className={classes.overline}>Access</Typography>
							<Typography className={classes.subtitle}>{trainingClass?.access}</Typography>
						</div>
					</div>
				</>
			</CardContent>
		</Card>
	)
}

export default TrainingClassDetailsCard
